// Section « Disponibilité » de la fiche appareil — une case par heure.
//
// Ce que la grille montre, c'est l'HEURE À LAQUELLE LA MACHINE EST ALLUMÉE. Les
// heures sont en colonnes et les jours en lignes, de sorte que l'habitude se lit
// verticalement : une bande verte de 8 h à 18 h sur cinq lignes et deux lignes
// vides, c'est un poste de bureau ; un rectangle plein, c'est un serveur.
//
// La mesure est le nombre de heartbeats reçus dans l'heure, rapporté au nombre
// attendu (l'intervalle configuré). Elle dit donc « l'agent tournait », ce qui
// pour un poste revient à « la machine était allumée » — pas « quelqu'un était
// devant ». Pour ça, il y a les ouvertures de session dans le journal d'appareil.

import { useEffect, useState } from 'react';
import { Activity } from 'lucide-react';
import { api } from '../lib/api.jsx';

const HOUR_MS = 3600000;

// Trois niveaux et pas davantage. La question posée est « allumée ou non » ;
// une échelle fine ferait hésiter sur des nuances qui ne veulent rien dire —
// 40 % ou 60 % d'une heure, c'est la même information : un démarrage ou un arrêt
// est tombé dedans.
const LEVELS = {
  up: { cls: 'bg-ok', label: 'En ligne' },
  partial: { cls: 'bg-warn', label: 'Partiel' },
  down: { cls: 'bg-rule-2', label: 'Hors ligne' },
  none: { cls: 'bg-transparent border border-rule', label: 'Pas de donnée' },
};

const RANGES = [
  { days: 7, label: '7 jours' },
  { days: 14, label: '14 jours' },
  { days: 30, label: '30 jours' },
];

// La fenêtre choisie suit l'utilisateur d'une fiche à l'autre et d'une session à
// la suivante : qui surveille un parc de serveurs veut ses 30 jours partout, pas
// à re-sélectionner à chaque appareil ouvert. Même convention que `mh_theme` et
// `mh_nic_filter` — préférence d'affichage, donc localStorage, donc portée au
// profil de navigateur.
const DAYS_KEY = 'mh_uptime_days';
const DEFAULT_DAYS = 7;

// Relue et VALIDÉE contre les options offertes, comme `readTheme`. Une valeur
// restée d'une version qui proposait d'autres fenêtres (ou modifiée à la main)
// partirait sinon telle quelle dans la requête.
function readDays() {
  const stored = Number(localStorage.getItem(DAYS_KEY));
  return RANGES.some((r) => r.days === stored) ? stored : DEFAULT_DAYS;
}

const pad2 = (n) => String(n).padStart(2, '0');

export function UptimeHeatmap({ agentId, sectionCls, sTitleCls }) {
  const [days, setDays] = useState(readDays);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // 24 h de marge au-delà de la fenêtre affichée : les seaux sont des heures
    // UTC et les lignes sont des jours LOCAUX, donc le premier jour local
    // commence avant le premier seau demandé dès que le fuseau n'est pas UTC.
    api.get(`/devices/${agentId}/uptime?hours=${days * 24 + 24}`).then((r) => {
      if (cancelled) return;
      setData(r);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [agentId, days]);

  if (loading) {
    return (
      <div className={sectionCls}>
        <div className={sTitleCls}>
          <Activity size={16} strokeWidth={1.75} /> Disponibilité
        </div>
        <div className="text-muted-2 text-xs">Chargement...</div>
      </div>
    );
  }
  if (!data) return null;

  const startMs = new Date(data.startUtc).getTime();
  const expected = data.expectedPerHour || 60;
  const registeredMs = data.registeredAt ? new Date(data.registeredAt).getTime() : 0;
  const now = Date.now();
  const currentHourMs = Math.floor(now / HOUR_MS) * HOUR_MS;

  // Une heure déjà entamée ne peut pas avoir reçu ses 60 signaux : la rapporter
  // au total attendu la peindrait « partielle » à chaque rafraîchissement.
  const expectedAt = (hourMs) => {
    if (hourMs !== currentHourMs) return expected;
    const elapsed = (now - currentHourMs) / HOUR_MS;
    return Math.max(1, Math.round(expected * elapsed));
  };

  const cellAt = (hourMs) => {
    const idx = Math.round((hourMs - startMs) / HOUR_MS);
    if (hourMs > currentHourMs) return { level: 'none', future: true };
    if (idx < 0 || idx >= data.hourCount) return { level: 'none' };

    const got = data.samples[idx] || 0;
    // L'enregistrement de l'agent ne sert qu'à EXPLIQUER un zéro, jamais à
    // masquer une mesure : des signaux antérieurs à la date d'enregistrement
    // existent (ré-enregistrement après réinstallation, agent recréé) et les
    // cacher effacerait de l'histoire réellement observée. À zéro en revanche,
    // « la machine était éteinte » et « l'agent n'existait pas » ne se
    // distinguent que par cette date.
    if (got === 0 && registeredMs && hourMs + HOUR_MS <= registeredMs)
      return { level: 'none', before: true };

    const exp = expectedAt(hourMs);
    const coverage = Math.min(1, got / exp);
    const level = got === 0 ? 'down' : coverage >= 0.9 ? 'up' : 'partial';
    return { level, coverage, got };
  };

  // ─── Lignes : un jour local chacune, de la plus ancienne à aujourd'hui ───
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const rows = [];
  for (let d = days - 1; d >= 0; d--) {
    const dayStart = new Date(today);
    dayStart.setDate(today.getDate() - d);
    const cells = [];
    for (let h = 0; h < 24; h++) {
      const hourDate = new Date(dayStart);
      hourDate.setHours(h);
      cells.push({ ...cellAt(hourDate.getTime()), date: hourDate, hour: h });
    }
    rows.push({ dayStart, cells, isToday: d === 0 });
  }

  // ─── Disponibilité globale sur ce qui est réellement mesuré ───
  // Formulé « allumé X % du temps » et non « disponibilité X % » : un poste
  // éteint toutes les nuits tourne 20 % du temps sans que rien n'aille mal, et
  // le mot « disponibilité » lui collerait un verdict de panne.
  const measured = rows.flatMap((r) => r.cells).filter((c) => c.level !== 'none');
  const availability = measured.length
    ? Math.round((measured.reduce((sum, c) => sum + (c.coverage || 0), 0) / measured.length) * 1000) / 10
    : null;

  // ─── Plage d'activité habituelle ───
  // Pour chaque heure du jour, la part des jours où la machine tournait au moins
  // à moitié. On ne conclut QUE si les heures retenues forment une plage
  // continue : un profil en deux blocs (ou qui enjambe minuit) se résumerait en
  // « 0 h → 23 h », ce qui serait faux. Dans ce cas la grille parle d'elle-même.
  // Le dénominateur ne compte QUE les jours où la machine s'est allumée. Un
  // week-end ou un congé ne dit rien sur les HEURES d'utilisation, seulement
  // qu'il n'y en a pas eu ; les garder au dénominateur fait tomber tout poste de
  // bureau sous n'importe quel seuil (5 jours ouvrés sur 7, moins un congé, et
  // l'heure la plus régulière plafonne à 57 %).
  let usualRange = null;
  const usedDays = rows.filter((r) => r.cells.some((c) => (c.got || 0) > 0));
  const perHour = [];
  for (let h = 0; h < 24; h++) {
    const cells = usedDays.map((r) => r.cells[h]).filter((c) => c.level !== 'none');
    perHour.push(cells.length ? cells.filter((c) => (c.coverage || 0) >= 0.5).length / cells.length : null);
  }
  const active = perHour.map((v) => v !== null && v >= 0.6);
  const activeCount = active.filter(Boolean).length;
  if (activeCount > 0 && activeCount < 24) {
    const first = active.indexOf(true);
    const last = active.lastIndexOf(true);
    if (last - first + 1 === activeCount) usualRange = { first, last };
  }

  return (
    <div className={sectionCls}>
      <div className={`${sTitleCls} justify-between`}>
        <span className="inline-flex items-center gap-2">
          <Activity size={16} strokeWidth={1.75} /> Disponibilité
          {availability !== null && (
            <span className="text-[11px] font-normal text-muted">
              — allumé {availability} % du temps
            </span>
          )}
        </span>
        <select
          value={days}
          onChange={(e) => {
            const next = Number(e.target.value);
            localStorage.setItem(DAYS_KEY, String(next));
            setDays(next);
          }}
          className="bg-sidebar border border-rule rounded-lg px-2 py-1 text-muted text-[11px] outline-none font-normal"
        >
          {RANGES.map((r) => (
            <option key={r.days} value={r.days}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap gap-6 items-start">
        <div className="overflow-x-auto">
          <div className="inline-block">
            {/* En-têtes d'heures, une graduation sur trois */}
            <div className="flex gap-[2px] mb-1 ml-[52px]">
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} className="w-[16px] text-[9px] text-muted-3 text-left leading-none">
                  {h % 3 === 0 ? pad2(h) : ''}
                </div>
              ))}
            </div>

            {rows.map((row) => (
              <div key={row.dayStart.getTime()} className="flex gap-[2px] mb-[2px] items-center">
                <div
                  className={`w-[48px] mr-1 text-[10px] text-right leading-none ${
                    row.isToday ? 'text-fg-2 font-semibold' : 'text-muted-3'
                  }`}
                >
                  {row.isToday
                    ? "auj."
                    : row.dayStart.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}
                </div>
                {row.cells.map((c) => (
                  <div
                    key={c.hour}
                    className={`w-[16px] h-[16px] rounded-[3px] ${LEVELS[c.level].cls}`}
                    title={
                      c.level === 'none'
                        ? `${c.date.toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                          })}, ${pad2(c.hour)} h — ${
                            c.future ? 'à venir' : c.before ? 'avant l’enregistrement de l’agent' : 'pas de donnée'
                          }`
                        : `${c.date.toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                          })}, ${pad2(c.hour)} h — ${Math.round((c.coverage || 0) * 100)} % (${c.got} signaux)`
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="text-[11px] text-muted-2 space-y-2 min-w-[180px]">
          <div className="space-y-1">
            {Object.entries(LEVELS).map(([key, lvl]) => (
              <div key={key} className="flex items-center gap-2">
                <span className={`w-[12px] h-[12px] rounded-[3px] inline-block ${lvl.cls}`} />
                {lvl.label}
              </div>
            ))}
          </div>
          {usualRange && (
            <div className="pt-2 border-t border-rule">
              <div className="text-muted-3 text-[10px] uppercase tracking-wide">Plage habituelle</div>
              <div className="text-fg-2 font-semibold text-xs mt-0.5">
                {pad2(usualRange.first)} h → {pad2(usualRange.last)} h
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
